import { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Context } from './AuthContext';

// 1. Create the Context
const CategoryContext = createContext();

// 2. Create a custom hook to use the Context more easily
export const useCategory = () => useContext(CategoryContext);

const fetchCategoryCounts = async (id, user) => {
  const token = await user.getIdToken();
  const url = process.env.REACT_APP_FLASK_WEBSERVER + 'get_category_counts?vertical_id=' + id;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 403) {
    return null;
  }
  const data = await res.json();
  return data;
};

const fetchVerticalNames = async (user) => {
  const token = await user.getIdToken();
  const url = process.env.REACT_APP_FLASK_WEBSERVER + 'get_vertical_names';
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    return [];
  }

  const data = await res.json();
  return Array.isArray(data) ? [...data].reverse() : [];
};

// 3. Create a provided component
export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState({});
  const [contextLoading, setContextLoading] = useState(true);
  const { user, authLoading } = useContext(Context);
  const { vertical_id } = useParams();
  const id = vertical_id;
  const [verticals, setVerticals] = useState([]);
  const [isEditor, setIsEditor] = useState(false);
  const [isAdvancedEditor, setIsAdvancedEditor] = useState(false);

  // Initial fetch to get category counts, only once on component mount
  useEffect(() => {
    if (!user || authLoading) return;
    fetchCategoryCounts(id, user).then((initialCounts) => {
      if (initialCounts) setCategories(initialCounts);
    });
  }, [id, user, authLoading]);

  useEffect(() => {
    if (!user || authLoading) return;
    fetchVerticalNames(user).then((verticals) => setVerticals(verticals));
  }, [user, authLoading]);

  const updateCount = (categoryId, change) => {
    // setCategories((prevCategories) => prevCategories.map((category) => (category.id === categoryId ? { ...category, count: newCount } : category)));
    setCategories((prevCategories) => ({
      ...prevCategories, // Copy all previous keys and values
      [categoryId]: categories[categoryId] + change,
    }));
  };

  const refreshCategoryCounts = (id) => {
    if (!user || authLoading) return;
    fetchCategoryCounts(id, user).then((initialCounts) => {
      if (initialCounts) setCategories(initialCounts);
    });
  };

  const refreshVerticals = () => {
    if (!user || authLoading) return;
    fetchVerticalNames(user).then((verticals) => setVerticals(verticals));
  };

  useEffect(() => {
    if (verticals.length > 0) {
      const editorStatus = verticals.some(
        (v) =>
          v.id === vertical_id &&
          (v.permission === 'Editor' ||
            v.permission === 'AdvancedEditor' ||
            v.status === 'Created')
      );
      const advancedEditorStatus = verticals.some(
        (v) =>
          v.id === vertical_id &&
          (v.permission === 'AdvancedEditor' || v.status === 'Created')
      );
      setIsEditor(editorStatus);
      setIsAdvancedEditor(advancedEditorStatus);
      setContextLoading(false);
    }
  }, [verticals, vertical_id]);

  // 4. Value provided to the Context consumers
  return (
    <CategoryContext.Provider
      value={{
        categories,
        updateCount,
        refreshCategoryCounts,
        verticals,
        refreshVerticals,
        isEditor,
        isAdvancedEditor,
        contextLoading,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};
